import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";

const BG = "/thinkavarium-ws.webp";

const TITLE = ["THINKAVARIUM"];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.4 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: d, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fgOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollDown = () =>
    document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" });

  // Live telemetry: fluctuate O₂ and TEMP slightly (±0.1 to 0.3)
  const [o2, setO2] = useState("20.9");
  const [temp, setTemp] = useState("23.0");
  useEffect(() => {
    const jitter = (base) => {
      const delta = (Math.random() * 0.2 + 0.1) * (Math.random() < 0.5 ? -1 : 1);
      return (base + delta).toFixed(1);
    };
    const id = setInterval(() => {
      setO2(jitter(20.9));
      setTemp(jitter(23));
    }, 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] overflow-hidden" data-testid="hero">
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 h-[120%] w-full"
      >
        <motion.img
          initial={{ opacity: 0, scale: 1.12 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
          src={BG}
          alt="THINKAVARIUM bunker"
          className="h-full w-full scale-125 object-cover object-bottom"
        />
        <div className="absolute inset-0 bg-void/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-transparent to-void" />
      </motion.div>

      {/* HUD corner labels */}
      <motion.div
        style={{ opacity: fgOpacity }}
        className="pointer-events-none absolute inset-0 z-20"
      >
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1.4}
          className="absolute left-6 top-28 font-mono text-[10px] tracking-[0.3em] text-hazard-amber md:left-10 md:text-[13px]">
          <div>LAT 84°42&apos;52.12&quot;S</div>
          <div>LON 114°34&apos;33.16&quot;W</div>
          <div className="mt-1 text-white/40">DEPTH −3229 M</div>
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1.5}
          className="absolute right-6 top-28 text-right font-mono text-[10px] tracking-[0.3em] text-porthole-blue md:right-10 md:text-[13px]">
          <div>REACTOR // NOMINAL</div>
          <div>
            O₂ <span className="tabular-nums text-porthole-blue">{o2}</span>% · TEMP{" "}
            <span className="tabular-nums text-porthole-blue">{temp}</span>°C
          </div>
          <motion.div
            className="mt-1 font-bold text-tactical-green md:text-[15px]"
            animate={{ opacity: [0.45, 1, 0.45] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          >
            SIGNAL SECURED
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Center title */}
      <motion.div
        style={{ y: titleY, opacity: fgOpacity }}
        className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center"
      >
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0.2}
          className="mb-5 flex items-center gap-3 font-mono text-[11px] tracking-[0.4em] text-reactor-red md:text-[14px]">
          <span className="hazard-stripes h-3 w-10" />
          CLASSIFIED · LEVEL 5 CLEARANCE
          <span className="hazard-stripes h-3 w-10" />
        </motion.div>

        <h1 className="font-octin leading-[0.8] tracking-tight text-white/90 text-shadow-hard">
          {TITLE.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={i}
                className="block whitespace-nowrap text-[8.25vw] md:text-[9vw] lg:text-[9.75vw]"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={1.1}
          className="mt-6 max-w-xl font-body text-sm font-light text-white/70 md:text-xl">
          A subterranean broadcast station transmitting new episodes from the deep.
          Enlist to intercept every signal before it goes dark.
        </motion.p>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1.3}
          className="mt-9 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
          <a
            href="#enlist"
            onClick={(e) => { e.preventDefault(); document.querySelector("#enlist")?.scrollIntoView({ behavior: "smooth" }); }}
            className="flex items-center justify-center border border-reactor-red px-6 py-3.5 font-mono text-[13px] font-bold tracking-[0.3em] text-reactor-red transition-colors duration-100 hover:bg-reactor-red hover:text-void md:text-[15px]"
            data-testid="hero-enlist-btn"
          >
            ► REQUEST ACCESS
          </a>
          <a
            href="#content"
            onClick={(e) => { e.preventDefault(); document.querySelector("#content")?.scrollIntoView({ behavior: "smooth" }); }}
            className="flex items-center justify-center border border-reactor-red px-6 py-3.5 font-mono text-[13px] font-bold tracking-[0.3em] text-reactor-red transition-colors duration-100 hover:bg-reactor-red hover:text-void md:text-[15px]"
            data-testid="hero-watch-btn"
          >
            ► LATEST TRANSMISSION
          </a>
          <Link
            to="/tip?type=topic_request"
            className="flex items-center justify-center border border-hazard-amber/70 px-6 py-3.5 font-mono text-[13px] font-bold tracking-[0.3em] text-hazard-amber transition-colors duration-100 hover:bg-hazard-amber hover:text-void md:text-[15px]"
            data-testid="hero-episode-idea-btn"
          >
            ► SUBMIT EPISODE IDEA
          </Link>
          <Link
            to="/tip?type=tip_off"
            className="flex items-center justify-center border border-hazard-amber/70 px-6 py-3.5 font-mono text-[13px] font-bold tracking-[0.3em] text-hazard-amber transition-colors duration-100 hover:bg-hazard-amber hover:text-void md:text-[15px]"
            data-testid="hero-tip-btn"
          >
            ► TIP-OFF LINE
          </Link>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.button
        onClick={scrollDown}
        style={{ opacity: fgOpacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 font-mono text-[10px] tracking-[0.4em] text-white/50 md:text-[13px]"
        data-testid="hero-scroll-cue"
      >
        <span className="mb-2 block">SCROLL TO DESCEND</span>
        <span className="mx-auto block h-8 w-px animate-pulse bg-hazard-amber" />
      </motion.button>
    </section>
  );
}
